/** @file The diff utility used in IO Test to render unified diff view. */
import { diff_match_patch as DiffMatchPatch } from 'diff-match-patch';
import { v4 as uuidv4 } from 'uuid';
import { escape } from './html';
import { visualizeWhitespace } from './visualize';
/**
 * Returns if the item was removed.
 *
 * @param d The item to check.
 * @returns If the item was removed.
 */
export function isRemoved(d) {
    return d[0] < 0;
}
/**
 * Returns if the item was the same.
 *
 * @param d The item to check.
 * @returns If the item was the same.
 */
export function isSame(d) {
    return d[0] === 0;
}
/**
 * Returns if the item was added.
 *
 * @param d The item to check.
 * @returns If the item was added.
 */
export function isAdded(d) {
    return d[0] > 0;
}
/** The default character used to render newline characters. */
export const DEFAULT_NEWLINE_CHAR = '¶';
/**
 * The default function to create a span element as string.
 *
 * @param cls The class to assign to the span.
 * @param inner The innerHTML of the span.
 * @returns The HTML as a string.
 */
function internalMakeSpan(cls, inner) {
    return `<span class="${cls}">${inner}</span>`;
}
/**
 * Diff between two strings.
 *
 * This will also clean up the diff so that it is somewhat easier for humans to understand.
 *
 * @param expected The string you expected, this is the base string on which edits will be done.
 * @param got The string that was received.
 * @returns The diff that allows you to go from `expected` to `got`.
 */
export function diff(expected, got) {
    const dmp = new DiffMatchPatch();
    const diffs = dmp.diff_main(got, expected, false);
    dmp.diff_cleanupSemantic(diffs);
    return diffs;
}
/**
 * Calculate the diff for an AutoTest IO Test step.
 *
 * @param argExpected The expected output.
 * @param argGot The actual output of the program that ran in AutoTest.
 * @param diffOptions Diffing options, see {@link IIOTestDiffOptions}.
 * @param renderOptions Render options, see {@link IIOTestRenderOptions}.
 * @returns A list of raw HTML strings, one for each line of the diff.
 */
function innerGetIOTestDiff(argExpected, argGot, diffOptions, renderOptions) {
    const { showIgnored, makeSpan = internalMakeSpan, visualizeWS = visualizeWhitespace, showCarriageAs, showNewLineAs = DEFAULT_NEWLINE_CHAR, } = renderOptions;
    /**
     * Create a temporary unique string to substitute for newline character.
     * Because we will escape the HTML in the user content later in this function.
     * If showNewLineAs is a HTML string it will be escaped.
     */
    const TEMP_NEWLINE_CHAR = `__CG_IO_TEST_DIFF_${uuidv4()}__`;
    let expected = argExpected;
    let got = argGot;
    if (diffOptions.caseInsensitive) {
        expected = argExpected.toLowerCase();
        got = argGot.toLowerCase();
    }
    const diffs = diff(expected, got);
    const lines = [''];
    if (diffOptions.substring && diffs.length >= 2) {
        /*
        If the last/first diff is an addition and the second/second to
        last is a deletion these can be swapped. This is only necessary
        as the deletion can be safely ignored if 'substring' match is
        enabled.
        */
        if (isRemoved(diffs[diffs.length - 2]) && isAdded(diffs[diffs.length - 1])) {
            const old = diffs[diffs.length - 2];
            diffs[diffs.length - 2] = diffs[diffs.length - 1];
            diffs[diffs.length - 1] = old;
        }
        if (isAdded(diffs[0]) && isRemoved(diffs[1])) {
            const old = diffs[1];
            diffs[1] = diffs[0];
            diffs[0] = old;
        }
    }
    diffs.forEach((diffPart, diffIndex) => {
        let cls = ''; // The class to be assigned to this diffPart
        if (isAdded(diffPart)) {
            cls = 'added'; // green background / missing output
        }
        else if (isRemoved(diffPart)) {
            // If substring is enabled and we are at the first or last item we
            // can safely ignore this entry.
            if (diffOptions.substring && (diffIndex === 0 || diffIndex === diffs.length - 1)) {
                cls = 'ignored'; // grey background / ignored output
            }
            else {
                cls = 'removed'; // red background / superfluous output
            }
        }
        if (!showIgnored && cls === 'ignored') {
            return;
        }
        /*
        Split a diffPart with multiple lines into an array of lines.
        There are many ways to split it depending on the options.
        */
        let splitted;
        // We use cls == 'removed' here and not state == REMOVED because we
        // don't need to do anything special (read slow) for ignored pieces.
        if (diffOptions.ignoreAllWhitespace && cls === 'removed') {
            splitted = diffPart[1].replace(/\n/g, '\0\n').split('\n');
        }
        else if (diffOptions.ignoreAllWhitespace && isAdded(diffPart)) {
            splitted = [diffPart[1].replace(/\n/g, '\0')];
        }
        else if ((diffOptions.ignoreTrailingWhitespace && cls === 'removed') || cls === 'added') {
            splitted = diffPart[1].replace(/\n/g, '\0\n').split('\n');
        }
        else if (cls) {
            splitted = diffPart[1].replace(/\n/g, `${TEMP_NEWLINE_CHAR}\n`).split('\n');
        }
        else {
            splitted = diffPart[1].split('\n');
        }
        /**
         * Function that encapsulate the condition when we want to ignore
         * trailing whitespace, and the next line is ignorable in that case.
         *
         * @param i The index of the current line in the `splitted` array.
         * @returns If we should ignore the whitespace in the line.
         */
        function nextIsNewline(i) {
            if (splitted.length - 1 > i) {
                return true;
            }
            // Last line is always followed by a newline (not really, but it is
            // the case for trailing whitespace)
            if (diffIndex === diffs.length - 1) {
                return true;
            }
            return diffs[diffIndex + 1][1][0] === '\n';
        }
        splitted.forEach((line, i) => {
            let toAdd = '';
            /**
             * Escape, visualize whitespace and render this piece of code as HTML.
             *
             * @param str The content.
             * @param spanCls The classes to assign to the HTML element.
             *
             * @returns The HTML rendered with `makeSpan`.
             */
            function highlight(str, spanCls = cls) {
                if (!str) {
                    return '';
                }
                const inner = visualizeWS(escape(str));
                return makeSpan(spanCls, inner);
            }
            // If we want to ignore all whitespace we want to mark all pieces of
            // whitespace in this line as ignored. This is of course only needed
            // if we would otherwise mark them as deleted or added.
            if (diffOptions.ignoreAllWhitespace && cls && cls !== 'ignored') {
                // XXX: Is it faster to use a reduce here, as we have quite some
                // cases where we don't really need to add anything to the
                // resulting array.
                toAdd = line
                    .split(/([\s\0]+)/g)
                    .map(tempPart => {
                    if (!tempPart) {
                        return '';
                    }
                    const whitePart = /(\s|\0)/.test(tempPart);
                    if (whitePart && !showIgnored) {
                        return '';
                    }
                    const part = tempPart.replace('\0', TEMP_NEWLINE_CHAR);
                    if (whitePart) {
                        return highlight(part, 'ignored');
                    }
                    else {
                        return highlight(part);
                    }
                })
                    .join('');
            }
            else if (diffOptions.ignoreTrailingWhitespace &&
                cls &&
                cls !== 'ignored' &&
                nextIsNewline(i) &&
                /\s+\0?$/.test(line)) {
                // If we need to ignore trailing whitespace and this line
                // contains trailing whitespace mark it as ignored.
                const [before, after] = line.split(/([\s\0]+)$/);
                const constainsNewlineChar = line.includes('\u0000');
                if (showIgnored) {
                    toAdd = highlight(before) + highlight(after.replace('\0', ''), 'ignored');
                }
                else {
                    toAdd = highlight(before);
                }
                if (constainsNewlineChar) {
                    toAdd += highlight(TEMP_NEWLINE_CHAR);
                }
            }
            else if (line && cls) {
                // Nothing special about this, simply highlight it.
                toAdd = highlight(line.replace(/\0/g, TEMP_NEWLINE_CHAR));
            }
            else if (line) {
                // We don't need to add any class to this line, so don't render
                // the span around it.
                toAdd = visualizeWS(escape(line));
            }
            // Replace tempNewLine (escape-safe string) to the real `showNewLineAs`
            toAdd = toAdd.replaceAll(TEMP_NEWLINE_CHAR, showNewLineAs);
            if (showCarriageAs != null) {
                toAdd = toAdd.replaceAll('\r', showCarriageAs);
            }
            // Pushing the lines array causes a newline to render. We want to do
            // this if the index is higher than 0 (this means we splitted on a
            // newline) but not if the state is ADDED as we simply render a viz via
            // `visualizeNewline()` to indicate that the student is missing a newline.
            if (i === 0 || isAdded(diffPart)) {
                if (toAdd) {
                    lines[lines.length - 1] += toAdd;
                }
            }
            else {
                lines.push(toAdd);
            }
        });
    });
    return Object.freeze(lines);
}
/**
 * Calculate the diff for an AutoTest v1 IO Test step.
 *
 * In AutoTest v1 the options passed to the diff are different in that this
 * wants a list of enabled option strings.
 *
 * @param expected The expected output.
 * @param got The actual output of the program that ran in AutoTest.
 * @param options List of diffing options to enable.
 * @param showIgnored Whether ignored parts of the diff should be shown.
 * @param makeSpan A function to create a <span> tag.
 * @param visualizeWS A function visualize whitespace.
 * @param showNewLineAs A string to visualize newline characters.
 * @returns A list of raw HTML strings, one for each line of the diff.
 */
export function getATv1IOTestDiff(expected, got, options, showIgnored, makeSpan = internalMakeSpan, visualizeWS = visualizeWhitespace, showNewLineAs = DEFAULT_NEWLINE_CHAR) {
    const diffOptions = {
        substring: options.find(o => o === 'substring') != null,
        caseInsensitive: options.find(o => o === 'case') != null,
        ignoreAllWhitespace: options.find(o => o === 'all_whitespace') != null,
        ignoreTrailingWhitespace: options.find(o => o === 'trailing_whitespace') != null,
    };
    return innerGetIOTestDiff(expected, got, diffOptions, {
        showIgnored,
        makeSpan,
        visualizeWS,
        showNewLineAs,
    });
}
/**
 * Calculate the diff for an AutoTest IO Test step.
 *
 * @param expected The expected output.
 * @param got The actual output of the program that ran in AutoTest.
 * @param diffOptions Diffing options, see {@link IIOTestDiffOptions}.
 * @param renderOptions Render options, see {@link IIOTestRenderOptions}.
 * @returns A list of raw HTML strings, one for each line of the diff.
 */
export function getATv2IOTestDiff(expected, got, diffOptions, renderOptions) {
    return innerGetIOTestDiff(expected, got, diffOptions, renderOptions);
}
